import { useState } from 'react';
import type { StoreApi } from 'zustand';
import { Check, MapPin, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/workspace-dialog';
import { MRS_CHIP_SHAPE } from '@/components/MrsLink';
import { cn } from '@/lib/utils';
import type { PostDialogState } from './postDialogStore';
import type { PostRow } from './postRows';

interface PostSerialsSheetProps {
  row: PostRow | null;
  store: StoreApi<PostDialogState>;
  onClose: () => void;
}

/**
 * PT-5 deliverable 6 — "Serials to post", stacked over the Post dialog (a nested layer: it renders inside the
 * dialog's content tree, so a click in it never dismisses the dialog).
 *
 * Every picked serial of the row is listed, available first (O-20 slot 15): a slot an open batch holds or that is
 * already posted is dimmed, tagged "Posting" / "Posted", and not tickable — the claim refuses such a slot (C5), and
 * the sheet never offers it. Cancel discards; Done writes the ticks. Done with none keeps the row selected at 0
 * (the ticket's rule), which the post then leaves out.
 */
export function PostSerialsSheet({ row, store, onClose }: PostSerialsSheetProps) {
  return (
    <Dialog open={row !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        hideCloseButton
        overlayClassName="bg-black/35"
        className="flex max-h-[min(640px,100%)] w-[520px] max-w-[calc(100%-48px)] flex-col gap-0 overflow-hidden rounded-[14px] bg-card p-0 sm:rounded-[14px]"
      >
        {row && <SheetBody row={row} store={store} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function SheetBody({ row, store, onClose }: { row: PostRow; store: StoreApi<PostDialogState>; onClose: () => void }) {
  // A draft: nothing reaches the dialog until Done.
  const [draft, setDraft] = useState<Record<number, boolean>>(() => {
    const ticked = store.getState().ticked;
    return Object.fromEntries(row.serials.filter((u) => u.available).map((u) => [u.slotId, Boolean(ticked[u.slotId])]));
  });
  const [query, setQuery] = useState('');

  const available = row.serials.filter((u) => u.available);
  const on = available.filter((u) => draft[u.slotId]).length;
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? row.serials.filter((u) => u.serial.toLowerCase().includes(needle) || u.bin.toLowerCase().includes(needle))
    : row.serials;
  const who = [row.requestorFullName, row.departmentName].filter(Boolean).join(' · ');

  const setAll = (value: boolean) => setDraft(Object.fromEntries(available.map((u) => [u.slotId, value])));
  const done = () => {
    const s = store.getState();
    s.setTicks(draft);
    // Done with at least one serial selects the row (the prototype's); with none it keeps the row's state.
    if (on > 0 && !s.selected[row.id]) s.toggleRow(row.id);
    onClose();
  };

  return (
    <>
      <div className="flex items-start justify-between gap-3 px-[18px] pt-4">
        <div className="min-w-0">
          <DialogTitle className="text-[15px] font-bold">Serials to post</DialogTitle>
          <div className="mt-[5px] flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="font-mono text-xs font-semibold text-foreground">{row.itemCode}</span>
            {/* Plain, not a hover-card chip (L1685). */}
            <span className={cn(MRS_CHIP_SHAPE, 'text-info')}>{row.mrsNumber}</span>
            {who && <span className="truncate" title={who}>{who}</span>}
          </div>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px] text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent>Close</TooltipContent>
        </Tooltip>
      </div>
      <DialogDescription className="mx-[18px] mt-2 text-xs leading-normal">
        Untick any serial you are not releasing now — it stays picked for a later batch.
      </DialogDescription>

      <div className="flex items-center gap-2 border-b border-border px-[18px] py-2.5">
        <span className="text-xs font-semibold tabular-nums">{on} of {available.length} selected</span>
        <Button variant="outline" size="sm" className="ml-auto h-[26px] px-[9px] text-[11.5px]" onClick={() => setAll(true)}>
          Select all
        </Button>
        <Button variant="outline" size="sm" className="h-[26px] px-[9px] text-[11.5px]" onClick={() => setAll(false)}>
          Clear
        </Button>
      </div>

      {row.serials.length > 8 && (
        <div className="border-b border-border px-[18px] py-2">
          {/* The rows are in hand: no request, no debounce. */}
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search serial or bin"
            aria-label="Search serial or bin"
            className="h-8 text-[12.5px] md:text-[12.5px]"
          />
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {shown.map((u) => {
          const checked = u.available && Boolean(draft[u.slotId]);
          return (
            <div
              key={u.slotId}
              role="checkbox"
              aria-checked={checked}
              aria-disabled={!u.available}
              aria-label={`${u.serial} in ${u.bin}${u.tag ? ` — ${u.tag.toLowerCase()}` : ''}`}
              tabIndex={u.available ? 0 : -1}
              onClick={() => u.available && setDraft((d) => ({ ...d, [u.slotId]: !d[u.slotId] }))}
              onKeyDown={(e) => {
                if (u.available && (e.key === ' ' || e.key === 'Enter')) {
                  e.preventDefault();
                  setDraft((d) => ({ ...d, [u.slotId]: !d[u.slotId] }));
                }
              }}
              className={cn(
                'box-border flex min-h-[42px] items-center gap-2.5 border-b border-border/60 px-[18px] py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                u.available ? 'cursor-pointer hover:bg-muted/40' : 'cursor-default opacity-55',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'inline-flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px]',
                  checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-card',
                )}
              >
                {checked && <Check className="h-[11px] w-[11px]" />}
              </span>
              <span className="font-mono text-[12.5px] font-semibold">{u.serial}</span>
              <span className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground">
                <MapPin className="h-3 w-3" aria-hidden />
                {u.bin}
              </span>
              {u.tag && (
                <span className="ml-auto inline-flex h-5 items-center rounded-full bg-muted px-2 text-[11px] font-medium text-muted-foreground">
                  {u.tag}
                </span>
              )}
            </div>
          );
        })}
        {shown.length === 0 && (
          <p className="p-[18px] text-center text-xs text-muted-foreground">No serial matches that search.</p>
        )}
      </div>

      <div className="flex justify-end gap-2 border-t border-border px-[18px] pb-3.5 pt-3">
        <Button variant="outline" className="h-9 text-[13px]" onClick={onClose}>
          Cancel
        </Button>
        <Button className="h-9 gap-1.5 text-[13px] font-semibold" onClick={done}>
          <Check className="h-3.5 w-3.5" aria-hidden />
          {on === 0 ? 'Post none of these' : `Use ${on} ${on === 1 ? 'serial' : 'serials'}`}
        </Button>
      </div>
    </>
  );
}
